import { HotelBoardLabel } from './HotelConditionLabel';
import { Autocomplete, FormControlLabel, Switch, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { HOTEL_BOARD_CATEGORIES, HOTEL_REFUNDABILITY, HotelSearchFilters } from '@bandido/shared/types';

export function HotelSearchFilterFields({
  value,
  onChange,
  disabled = false,
}: {
  value: HotelSearchFilters;
  onChange: (value: HotelSearchFilters) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Stack gap={1}>
      <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
        <Autocomplete
          multiple
          options={[...HOTEL_BOARD_CATEGORIES]}
          value={value.board_categories}
          disabled={disabled}
          onChange={(_event, board_categories) => onChange({ ...value, board_categories })}
          renderOption={(props, category) => (
            <li {...props}>
              <HotelBoardLabel category={category} />
            </li>
          )}
          renderTags={(categories, getTagProps) =>
            categories.map((category, index) => {
              const { key, ...props } = getTagProps({ index });
              return <HotelBoardLabel key={key} category={category} {...props} />;
            })
          }
          getOptionLabel={(category) => t(`hotelSearch.boards.${category}`)}
          sx={{ flex: 1, minWidth: 0 }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('hotelSearch.boardFilter')}
              placeholder={value.board_categories.length ? undefined : t('hotelSearch.anyBoard')}
            />
          )}
        />
        <TextField
          select
          size="small"
          label={t('hotelSearch.refundabilityFilter')}
          value={value.refundability ?? 'all'}
          disabled={disabled}
          sx={{ flex: 1, minWidth: 0 }}
          onChange={(event) =>
            onChange({
              ...value,
              refundability:
                event.target.value === 'all' ? null : (event.target.value as HotelSearchFilters['refundability']),
            })
          }
        >
          <MenuItem value="all">{t('hotelSearch.anyRefundability')}</MenuItem>
          {HOTEL_REFUNDABILITY.map((state) => (
            <MenuItem key={state} value={state}>
              {t(`hotelSearch.refundability.${state}`)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <FormControlLabel
        label={t('hotelSearch.includePackages')}
        control={
          <Switch
            checked={value.include_package_rates !== false}
            disabled={disabled}
            onChange={(_event, checked) => onChange({ ...value, include_package_rates: checked })}
          />
        }
      />
      <Typography variant="caption" color="text.secondary">
        {t('hotelSearch.filtersHelp')}
      </Typography>
    </Stack>
  );
}
